import { Dietary } from '@hungryroot/constants';
import { DietaryPrefs } from '@hungryroot/dietary-restrictions';
import { type ContainerFrequencyOptions } from '@hungryroot/rest';
import {
  ADULTS_COUNT_PROP,
  CHILDREN_COUNT_PROP,
  YOUNG_CHILDREN_COUNT_PROP,
} from '../questionProps';

export enum HouseholdSize {
  ME = 1,
  ME_PLUS_ONE = 2,
  ME_PLUS_MORE = 3,
}

export enum Goals {
  SaveTime = 5,
  ImproveHealth = 2,
  LoseWeight = 3,
  TryNewThings = 9,
  EatLessTakeout = 10,
  GroceryShopLess = 4,
  WasteLessFood = 11,
  SaveMoney = 7,
  EatMoreVeggiesWithFamily = 8,
  SimplifyCooking = 12,
  LowerStress = 13,
}

// Note: this definition must stay in lockstep with EatingHabit in app-mhr/src/pages/Preferences/types/eating-habit.ts
export enum EatingHabit {
  Omnivore = 'omnivore',
  Flexitarian = 'flexitarian',
  Pescatarian = 'pescatarian',
  Vegetarian = 'vegetarian',
  Vegan = 'vegan',
}

export const EatingHabitToDietKeys = {
  [EatingHabit.Omnivore]: DietaryPrefs.Omnivore,
  [EatingHabit.Flexitarian]: DietaryPrefs.Flexitarian,
  [EatingHabit.Pescatarian]: DietaryPrefs.Pescatarian,
  [EatingHabit.Vegetarian]: DietaryPrefs.Vegetarian,
  [EatingHabit.Vegan]: DietaryPrefs.Vegan,
};

export const DietaryToDietaryPrefs = {
  [Dietary.DairyFree]: DietaryPrefs.DairyFree,
  [Dietary.GlutenFree]: DietaryPrefs.GlutenFree,
  [Dietary.SoyFree]: DietaryPrefs.SoyFree,
  [Dietary.TreeNutFree]: DietaryPrefs.TreeNutFree,
  [Dietary.PeanutFree]: DietaryPrefs.PeanutFree,
  [Dietary.EggFree]: DietaryPrefs.EggFree,
  [Dietary.ShellfishFree]: DietaryPrefs.ShellfishFree,
  [Dietary.SesameFree]: DietaryPrefs.SesameFree,
  [Dietary.GrainFree]: DietaryPrefs.GrainFree,
  [Dietary.GarlicFree]: DietaryPrefs.GarlicFree,
  [Dietary.OnionFree]: DietaryPrefs.OnionFree,
  [Dietary.CaffeineFree]: DietaryPrefs.CaffeineFree,
};

export type TagId = number;

export enum FoodPrep {
  Simple = 'simple',
  ChoppingOrPrepping = 'chopping-or-prepping',
}

export interface ProteinTagValue {
  id: TagId;
  liked: LikeDislikeTagsRatings;
}

export interface ProteinTagTernaryValue {
  id: TagId;
  rated: ProteinTagTernaryRating;
}

export enum ProteinTagTernaryRating {
  NoThanks = -1,
  SoSo = 0,
  Absolutely = 1,
}

export const LikeDislikeTags = {
  NoRating: 0,
  Liked: 1,
  Disliked: -1,
} as const;

type LikeDislikeTagsRatings =
  (typeof LikeDislikeTags)[keyof typeof LikeDislikeTags];

const KID_SNACKS_QUESTION = 'kidsSnacks';
const KID_SNACKS_FREQUENCY_QUESTION = 'kidsSnacksFrequency';

type KidSnackQuiz<T> = {
  [KID_SNACKS_QUESTION]: number[];
  [KID_SNACKS_FREQUENCY_QUESTION]: T | null;
};

type Nullable<T> = { [K in keyof T]: T[K] | null };

export type LunchDinnerPick = { id: number; type?: 'product' | 'pairing' };

export interface SwipeRating {
  itemId: number;
  itemType: 'product' | 'pairing';
  // Breakfast/snack/sweet swipe cards are tag-backed (itemType 'tag') and are
  // never recorded here — `useRecordSwipeRating` tracks them in quiz.breakfast/
  // quiz.snacks/quiz.sweets instead, so 'snacks' | 'sweets' are not reachable
  // values (breakfast stays, since a breakfast *pairing* is still recorded).
  meal: 'lunch' | 'dinner' | 'breakfast';
  rating: 'positive' | 'negative' | 'skip';
  // Optional for backward-compat with quizzes persisted before this field
  // existed. Used to favorite positive pairing swipes on signup without an
  // extra lookup (see `deriveFunnelFavoritePairings`).
  name?: string;
}

// Exp 220 (Bento Box) Groceries display tiles. Stable slugs, so stored
// answers survive tile reordering.
export type BentoBoxGroceryCategory =
  | 'kitchen_staples'
  | 'snacks'
  | 'sweets'
  | 'beverages';

export type NonNullableQuiz = {
  householdSize: HouseholdSize;
  [ADULTS_COUNT_PROP]: number;
  [CHILDREN_COUNT_PROP]: number;
  [YOUNG_CHILDREN_COUNT_PROP]: number;
  goals: Goals[];
  healthGoals: TagId[];
  budget?: TagId;
  habits: EatingHabit;
  dietary: Dietary[];
  macroNutrient: TagId[];
  foodQualities: TagId[];
  foodFormats: TagId[];
  foodPref: FoodPrep;
  lunchPicks: LunchDinnerPick[];
  dinnerPicks: LunchDinnerPick[];
  swipeRatings: SwipeRating[];

  // Tag Based
  cuisines: ProteinTagValue[];
  flavors: ProteinTagValue[];
  meats: ProteinTagTernaryValue[];
  seafood: ProteinTagTernaryValue[];
  plants: ProteinTagTernaryValue[];
  dislike: TagId[];
  dislikedProductIds: number[];
  likedProductIds: number[];
  // The `EatingHabit` under which `meats`/`seafood`/`plants`/`dislike` were
  // last recorded on the Avoidances/ProteinPreferences screens. CLIENT-ONLY:
  // never sent to the server and never reconstructed on rehydrate (same
  // class as `dislikedProductIds`/`likedProductIds`/`swipeRatings`, which
  // `useQuizLoader` hardcodes to null). Used to gate the diet-change prune to
  // actual in-session habit transitions — see `useAvoidancesHabitPrune`.
  avoidancesHabitsSnapshot: EatingHabit;
  breakfast: TagId[];
  breakfastFrequency: ContainerFrequencyOptions;
  lunchDinner: TagId[];
  lunch?: TagId[];
  dinner?: TagId[];
  lunchFrequency: ContainerFrequencyOptions;
  dinnerFrequency: ContainerFrequencyOptions;
  readyMade: TagId[];
  snacks: TagId[];
  snacksFrequency: ContainerFrequencyOptions;
  sweets: TagId[];
  sweetsFrequency: ContainerFrequencyOptions;
  // Exp 220 Groceries tiles picked on the food formats screen. CLIENT-ONLY:
  // kept in local quiz storage for resume, never sent to the server or the
  // recommenders (payload builders read named fields only). The same picks
  // are tracked as `grocery_categories` on `Quiz Food Formats`.
  bentoBoxGroceryCategories?: BentoBoxGroceryCategory[];
} & Partial<KidSnackQuiz<ContainerFrequencyOptions>>;

export type Quiz = Nullable<NonNullableQuiz>;

export const defaultQuiz: Quiz = {
  householdSize: null,
  [ADULTS_COUNT_PROP]: 1,
  [CHILDREN_COUNT_PROP]: null,
  [YOUNG_CHILDREN_COUNT_PROP]: null,
  goals: null,
  healthGoals: null,
  budget: null,
  habits: null,
  dietary: null,
  macroNutrient: null,
  foodQualities: null,
  foodFormats: null,
  foodPref: null,
  cuisines: null,
  flavors: null,
  meats: null,
  seafood: null,
  plants: null,
  lunchPicks: null,
  dinnerPicks: null,
  swipeRatings: null,
  dislike: null,
  dislikedProductIds: null,
  likedProductIds: null,
  avoidancesHabitsSnapshot: null,
  breakfast: null,
  breakfastFrequency: null,
  lunchDinner: null,
  lunch: null,
  dinner: null,
  lunchFrequency: null,
  dinnerFrequency: null,
  readyMade: null,
  snacks: null,
  sweets: null,
  snacksFrequency: null,
  sweetsFrequency: null,
  [KID_SNACKS_FREQUENCY_QUESTION]: null,
};
